import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Check, Circle, Clock, Trash2, Edit2, Archive } from 'lucide-react';
import { Button, Card, Modal } from '../../components/UI';
import { goalsApi, todosApi } from '../../api';
import './GoalDetail.css';

const STATUS_CONFIG = {
  todo: { label: 'To Do', icon: Circle, color: 'slate' },
  in_progress: { label: 'In Progress', icon: Clock, color: 'gold' },
  done: { label: 'Done', icon: Check, color: 'forest' },
  archived: { label: 'Archived', icon: Archive, color: 'silver' }
};

const GoalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [goal, setGoal] = useState(null);
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'todo'
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [goalsRes, todosRes] = await Promise.all([
        goalsApi.getAll(),
        todosApi.getByGoal(id)
      ]);
      const foundGoal = goalsRes.data.find(g => g.id === parseInt(id));
      setGoal(foundGoal);
      setTodos(todosRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTodo) {
        await todosApi.update(editingTodo.id, formData);
      } else {
        await todosApi.create(id, formData);
      }
      setIsModalOpen(false);
      setEditingTodo(null);
      resetForm();
      fetchData();
      updateGoalProgress();
    } catch (error) {
      console.error('Error saving todo:', error);
    }
  };

  const handleStatusChange = async (todoId, newStatus) => {
    try {
      await todosApi.updateStatus(todoId, newStatus);
      fetchData();
      updateGoalProgress();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDelete = async (todoId) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await todosApi.delete(todoId);
        fetchData();
        updateGoalProgress();
      } catch (error) {
        console.error('Error deleting todo:', error);
      }
    }
  };

  const updateGoalProgress = async () => {
    // Auto-calculate progress based on completed todos
    const todosRes = await todosApi.getByGoal(id);
    const allTodos = todosRes.data;
    if (allTodos.length > 0) {
      const activeTodos = allTodos.filter(t => t.status !== 'archived');
      if (activeTodos.length === 0) return;
      const completedCount = activeTodos.filter(t => t.status === 'done').length;
      const progress = Math.round((completedCount / activeTodos.length) * 100);
      await goalsApi.updateProgress(id, progress);
    }
  };

  const handleEdit = (todo) => {
    setEditingTodo(todo);
    setFormData({
      title: todo.title,
      description: todo.description || '',
      status: todo.status
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      status: 'todo'
    });
  };

  const openNewModal = () => {
    setEditingTodo(null);
    resetForm();
    setIsModalOpen(true);
  };

  const getTodosByStatus = (status) => {
    return todos.filter(t => t.status === status);
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!goal) {
    return (
      <div className="empty-state">
        <h3>Goal not found</h3>
        <Button onClick={() => navigate('/goals')}>Back to Goals</Button>
      </div>
    );
  }

  return (
    <div className="goal-detail-page">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/goals')}>
          <ArrowLeft size={20} />
          <span>Back to Goals</span>
        </button>
      </div>

      <div className="goal-info">
        <h1>{goal.title}</h1>
        {goal.description && <p className="goal-description">{goal.description}</p>}
        <div className="goal-progress-bar">
          <div className="progress-fill" style={{ width: `${goal.progress}%` }} />
        </div>
        <span className="progress-text">{goal.progress}% Complete</span>
      </div>

      <div className="todos-header">
        <h2>Tasks</h2>
        <div className="todos-actions">
          <div className="view-toggle">
            <button 
              className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
            <button 
              className={`toggle-btn ${viewMode === 'kanban' ? 'active' : ''}`}
              onClick={() => setViewMode('kanban')}
            >
              Kanban
            </button>
          </div>
          <Button icon={Plus} onClick={openNewModal}>Add Task</Button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="todos-list">
          {todos.length === 0 ? (
            <div className="empty-todos">
              <p>No tasks yet. Add your first task to get started!</p>
              <Button icon={Plus} onClick={openNewModal}>Add Task</Button>
            </div>
          ) : (
            todos.map((todo) => {
              const StatusIcon = STATUS_CONFIG[todo.status].icon;
              return (
                <Card key={todo.id} className={`todo-item status-${todo.status}`}>
                  <div className="todo-content">
                    <button 
                      className={`status-btn ${todo.status}`}
                      onClick={() => {
                        const nextStatus = todo.status === 'todo' ? 'in_progress' : 
                                          todo.status === 'in_progress' ? 'done' : 
                                          todo.status === 'done' ? 'todo' : 'todo';
                        handleStatusChange(todo.id, nextStatus);
                      }}
                    >
                      <StatusIcon size={20} />
                    </button>
                    <div className="todo-text">
                      <h4 className={todo.status === 'done' ? 'completed' : ''}>{todo.title}</h4>
                      {todo.description && <p>{todo.description}</p>}
                    </div>
                  </div>
                  <div className="todo-actions">
                    <select 
                      value={todo.status} 
                      onChange={(e) => handleStatusChange(todo.id, e.target.value)}
                      className="status-select"
                    >
                      <option value="todo">To Do</option>
                      <option value="in_progress">In Progress</option>
                      <option value="done">Done</option>
                      <option value="archived">Archived</option>
                    </select>
                    <button className="action-btn" onClick={() => handleEdit(todo)}>
                      <Edit2 size={16} />
                    </button>
                    <button className="action-btn danger" onClick={() => handleDelete(todo.id)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      ) : (
        <div className="kanban-board">
          {Object.entries(STATUS_CONFIG).map(([status, config]) => {
            const StatusIcon = config.icon;
            const statusTodos = getTodosByStatus(status);
            return (
              <div key={status} className={`kanban-column status-${status}`}>
                <div className="column-header">
                  <StatusIcon size={18} />
                  <h3>{config.label}</h3>
                  <span className="count">{statusTodos.length}</span>
                </div>
                <div className="column-content">
                  {statusTodos.map((todo) => (
                    <Card key={todo.id} className="kanban-card">
                      <h4>{todo.title}</h4>
                      {todo.description && <p>{todo.description}</p>}
                      <div className="kanban-card-actions">
                        <select 
                          value={todo.status} 
                          onChange={(e) => handleStatusChange(todo.id, e.target.value)}
                          className="status-select-mini"
                        >
                          <option value="todo">To Do</option>
                          <option value="in_progress">In Progress</option>
                          <option value="done">Done</option>
                          <option value="archived">Archived</option>
                        </select>
                        <button className="action-btn-mini" onClick={() => handleEdit(todo)}>
                          <Edit2 size={14} />
                        </button>
                        <button className="action-btn-mini danger" onClick={() => handleDelete(todo.id)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </Card>
                  ))}
                  {statusTodos.length === 0 && (
                    <div className="empty-column">No tasks</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTodo(null);
          resetForm();
        }}
        title={editingTodo ? 'Edit Task' : 'Add New Task'}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Task Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="What needs to be done?"
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add more details..."
              rows={3}
            />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="todo">To Do</option>
              <option value="in_progress">In Progress</option>
              <option value="done">Done</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="gold">
              {editingTodo ? 'Save Changes' : 'Add Task'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default GoalDetail;
